import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { useProjects, projectsKey } from "./useProjects";

const STORAGE_KEY = "callaloo_seeded_demo_v1";

/**
 * First-run onboarding: when a brand-new org logs in for the first time
 * and the project list is empty, POST /v1/admin/seed-demo so they have
 * something to click through.
 *
 * Idempotent on the server (reuses the existing demo if present), but we
 * also gate on a localStorage flag to avoid the round-trip after the
 * first time.
 */
export function useOnboarding(): void {
  const { data, isLoading } = useProjects();
  const qc = useQueryClient();

  useEffect(() => {
    if (isLoading || !data) return;
    if (!data.projects || data.projects.length > 0) return;
    try {
      if (localStorage.getItem(STORAGE_KEY) === "done") return;
    } catch {
      return; // localStorage unavailable — skip silently.
    }

    void (async () => {
      try {
        await customFetch<{ projectId: string; reused: boolean }>("/api/v1/admin/seed-demo", {
          method: "POST",
        });
        try {
          localStorage.setItem(STORAGE_KEY, "done");
        } catch {
          /* noop */
        }
        qc.invalidateQueries({ queryKey: projectsKey });
      } catch {
        // If seeding fails (rate limit, network), don't block the UI; the
        // user can still create projects manually. We'll retry on the next
        // mount because the storage flag won't be set.
      }
    })();
  }, [isLoading, data, qc]);
}
